import { api } from "@/lib/axios";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import {
  CodeName,
  CodeNameParams,
  Pageable,
  ReportData,
  ReportParams,
  SaleRank,
  SaleRankParams,
  SaleSlip,
  SaleTopList,
  SaleTopListParams,
  SaleTotal,
} from "@/interface";
import { toast } from "sonner";

export const useSaleTopList = (
  params: SaleTopListParams,
  options?: Omit<UseQueryOptions<SaleTopList[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery<SaleTopList[]>({
    queryKey: ["saleTopList", params],
    queryFn: () => {
      return api.post("/sale-slip/top-monthly", null, { params });
    },
    select: (data: SaleTopList[]) => data,
    ...options,
  });
};

export const useSaleSlipOrderCode = (params: string) => {
  return useQuery<SaleSlip | null>({
    queryKey: ["saleSlipOrderCode", params],
    queryFn: () => api.get("/sale-slip/order-code", { params: { orderCode: params } }),
    select: (data: SaleSlip | null) => data,
    enabled: !!params, // params가 있을 때만 쿼리 실행
    staleTime: 0, // 항상 새로운 데이터 조회
    gcTime: 0, // 캐시 비활성화
  });
};

export const useCodeName = (
  params: CodeNameParams,
  options?: Omit<UseQueryOptions<CodeName[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery<CodeName[]>({
    queryKey: ["codeName", params],
    queryFn: () => api.get("/sale-slip/code-name", { params }),
    select: (data: CodeName[]) => data,
    ...options,
  });
};

export const useSaleTotal = () => {
  return useQuery<SaleTotal>({
    queryKey: ["saleYtdSummary"],
    queryFn: () => api.get("/sale-slip/summary-ytd"),
    select: (data: SaleTotal) => data,
  });
};

export const useSaleRank = (
  params: SaleRankParams,
  options?: Omit<UseQueryOptions<SaleRank[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery<SaleRank[]>({
    queryKey: ["saleRank", params],
    queryFn: () => {
      const { companyCodes, ...queryParams } = params;
      // 쿼리 파라미터: dateFrom, dateTo
      // Body: companyCodes 배열
      return api.post("/sale-slip/rank", companyCodes, { params: queryParams });
    },
    select: (data: SaleRank[]) => data,
    ...options,
  });
};

export const useReportList = (
  params: ReportParams,
  options?: Omit<UseQueryOptions<Pageable<ReportData>, Error>, "queryKey" | "queryFn">
) => {
  return useQuery<Pageable<ReportData>>({
    queryKey: ["reportList", params],
    queryFn: () => api.get("/sale-slip/report", { params }),
    select: (data: Pageable<ReportData>) => data,
    ...options,
  });
};

export const useReportOne = (
  saleSlipCode: string,
  options?: Omit<UseQueryOptions<ReportData, Error>, "queryKey" | "queryFn">
) => {
  return useQuery<ReportData>({
    queryKey: ["reportOne", saleSlipCode],
    queryFn: async () => {
      // api.get은 이미 res.data를 반환하므로 response 자체가 데이터
      const response = await api.get<ReportData>("/sale-slip/report-one", {
        params: { saleSlipCode },
      });
      return response;
    },
    enabled: !!saleSlipCode && options?.enabled !== false,
    ...options,
  });
};

/**
 * 매출 전표 잠금/해제
 */
export const useUpdateSaleSlipLock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ saleSlipCode, lock }: { saleSlipCode: string; lock: boolean }) => {
      return api.put(`/sale-slip/${saleSlipCode}/lock`, null, {
        params: { lock },
      });
    },
    onSuccess: (_, variables) => {
      // 성공 시 관련 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: ["reportList"] });
      queryClient.invalidateQueries({ queryKey: ["reportOne"] });
      toast.success(variables.lock ? "잠금되었습니다." : "해제되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "실패했습니다.");
    },
  });
};

export const insertAndUpdateSaleSlip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SaleSlip) => api.post("/sale-slip", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reportList"] });
      toast.success("저장되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "실패했습니다.");
    },
  });
};

/**
 * 매출 전표 소프트 삭제 (복수 선택 가능, delYn을 'Y'로 변경)
 */
export const useDeleteSaleSlip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      saleSlipCodes,
      orderSlipCodes,
      delId,
    }: {
      saleSlipCodes: string[];
      orderSlipCodes?: string[];
      delId: string;
    }) => {
      return api.delete("/sale-slip/soft", {
        data: {
          saleSlipCodes,
          orderSlipCodes: orderSlipCodes || [],
        },
        params: { delId },
      });
    },
    onSuccess: res => {
      // 모든 reportList 관련 쿼리 무효화하여 자동 새로고침
      queryClient.invalidateQueries({ queryKey: ["reportList"], refetchType: "all" });
      // orderSaleSlipReport 쿼리도 무효화 (Sales.tsx에서 사용)
      queryClient.invalidateQueries({ queryKey: ["orderSaleSlipReport"], refetchType: "all" });
      toast.success("삭제되었습니다.");
    },
    onError: (error: any) => {
      console.error("❌ 삭제 실패:", error);
      toast.error(error?.response?.data?.message || "삭제에 실패했습니다.");
    },
  });
};

/**
 * 매출 전표 하드 삭제 (복수 선택 가능, 완전 삭제)
 */
export const useHardDeleteSaleSlip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ saleSlipCodes, orderSlipCodes }: { saleSlipCodes: string[]; orderSlipCodes?: string[] }) => {
      return api.delete("/sale-slip/hard", {
        data: {
          saleSlipCodes,
          orderSlipCodes: orderSlipCodes || [],
        },
      });
    },
    onSuccess: () => {
      // 모든 reportList 관련 쿼리 무효화하여 자동 새로고침
      queryClient.invalidateQueries({ queryKey: ["reportList"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["orderSaleSlipReport"], refetchType: "all" });
      toast.success("영구 삭제되었습니다.");
    },
    onError: (error: any) => {
      console.error("❌ 영구 삭제 실패:", error);
      toast.error(error?.response?.data?.message || "영구 삭제에 실패했습니다.");
    },
  });
};

/**
 * 매출 전표 복구 (복수 선택 가능, delYn을 'N'으로 변경)
 */
export const useRestoreSaleSlip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      saleSlipCodes,
      orderSlipCodes,
      delId,
    }: {
      saleSlipCodes: string[];
      orderSlipCodes?: string[];
      delId: string;
    }) => {
      return api.delete("/sale-slip/restore", {
        data: {
          saleSlipCodes,
          orderSlipCodes: orderSlipCodes || [],
        },
        params: { delId },
      });
    },
    onSuccess: () => {
      // 모든 reportList 관련 쿼리 무효화하여 자동 새로고침
      queryClient.invalidateQueries({ queryKey: ["reportList"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["orderSaleSlipReport"], refetchType: "all" });
      toast.success("복구되었습니다.");
    },
    onError: (error: any) => {
      console.error("❌ 복구 실패:", error);
      toast.error(error?.response?.data?.message || "복구에 실패했습니다.");
    },
  });
};

/**
 * 매출 전표 Excel 업로드 응답
 */
export interface SaleSlipExcelUploadResponse {
  success: boolean;
  message: string;
  totalRows?: number;
  savedCount?: number;
  errorCount?: number;
  errorFileBase64?: string;
  errorFileName?: string;
}

/**
 * 매출 전표 Excel 업로드
 */
export const useUploadSaleSlipExcel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["uploadSaleSlipExcel"],
    mutationFn: async ({ file, writeId }: { file: File; writeId: string }): Promise<SaleSlipExcelUploadResponse> => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("writeId", writeId);

      const response = await api.post("/sale-slip/upload-excel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response as unknown as SaleSlipExcelUploadResponse;
    },
    onSuccess: () => {
      // 성공 시 관련 쿼리 무효화하여 리스트 새로고침
      queryClient.invalidateQueries({ queryKey: ["reportList"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["orderSaleSlipReport"], refetchType: "all" });
    },
  });
};

/**
 * 매출 전표 Excel 업로드 양식 다운로드
 */
export const downloadSaleSlipUploadTemplate = async () => {
  try {
    const response = await api.get("/sale-slip/upload-template", {
      responseType: "blob",
    });

    const blob = new Blob([response as unknown as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // 파일명에 현재 날짜/시간 추가 (년월일시분 형식)
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `매출일보_${dateStr}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("업로드 양식을 다운로드했습니다.");
  } catch (error) {
    console.error("Failed to download template:", error);
    toast.error("업로드 양식 다운로드에 실패했습니다.");
  }
};
